/**
 * Article page enhancements: H2 table of contents, active section tracking,
 * and reading progress bar.
 */
(function () {
  const REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const DESKTOP_MQ = window.matchMedia('(min-width: 990px)');

  function slugify(text) {
    return text
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'section';
  }

  function uniqueId(base, used) {
    let id = base;
    let n = 2;
    while (used.has(id) || document.getElementById(id)) {
      id = `${base}-${n}`;
      n += 1;
    }
    used.add(id);
    return id;
  }

  function buildToc(headings, lists) {
    const used = new Set();

    headings.forEach((heading) => {
      if (!heading.id) {
        heading.id = uniqueId(slugify(heading.textContent), used);
      } else {
        used.add(heading.id);
      }
    });

    lists.forEach((list) => {
      list.replaceChildren();
      headings.forEach((heading) => {
        const li = document.createElement('li');
        li.className = 'article-toc__item';
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.className = 'article-toc__link';
        link.textContent = heading.textContent.trim();
        link.dataset.tocTarget = heading.id;
        li.appendChild(link);
        list.appendChild(li);
      });
    });
  }

  function setActiveLink(links, id) {
    links.forEach((link) => {
      const isActive = link.dataset.tocTarget === id;
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'location');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  function observeHeadings(headings, links) {
    if (!headings.length) return null;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length) {
          setActiveLink(links, visible[0].target.id);
          return;
        }

        // Fallback: nearest heading above the middle of the viewport
        const mid = window.innerHeight * 0.35;
        let current = headings[0];
        for (const heading of headings) {
          if (heading.getBoundingClientRect().top <= mid) {
            current = heading;
          }
        }
        if (current) setActiveLink(links, current.id);
      },
      {
        rootMargin: '-20% 0px -55% 0px',
        threshold: [0, 0.25, 1],
      }
    );

    headings.forEach((heading) => observer.observe(heading));
    return observer;
  }

  function bindTocClicks(root, links) {
    const mobileDetails = root.querySelector('[data-article-toc-mobile]');

    links.forEach((link) => {
      link.addEventListener('click', (event) => {
        const id = link.dataset.tocTarget;
        const target = id ? document.getElementById(id) : null;
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({
          behavior: REDUCE_MOTION ? 'auto' : 'smooth',
          block: 'start',
        });
        history.replaceState(null, '', `#${id}`);
        setActiveLink(links, id);

        if (mobileDetails && !DESKTOP_MQ.matches && mobileDetails.open) {
          mobileDetails.open = false;
        }
      });
    });
  }

  function updateProgress(progressEl, trackEl) {
    if (!progressEl || !trackEl) return;

    const rect = trackEl.getBoundingClientRect();
    const trackTop = window.scrollY + rect.top;
    const trackHeight = trackEl.offsetHeight;
    const viewport = window.innerHeight;
    const start = trackTop;
    const end = trackTop + trackHeight - viewport * 0.25;
    const range = Math.max(end - start, 1);
    const raw = ((window.scrollY - start) / range) * 100;
    const value = Math.min(100, Math.max(0, Math.round(raw)));

    progressEl.style.setProperty('--article-progress', `${value}%`);
    progressEl.setAttribute('aria-valuenow', String(value));
  }

  function initArticle(root) {
    const content = root.querySelector('[data-article-content]');
    if (!content) return;

    const showToc = root.dataset.showToc === 'true';
    const showProgress = root.dataset.showProgress === 'true';
    const headings = Array.from(content.querySelectorAll('h2'));
    const desktopToc = root.querySelector('[data-article-toc-desktop]');
    const mobileToc = root.querySelector('[data-article-toc-mobile]');
    const lists = Array.from(root.querySelectorAll('[data-article-toc-list]'));
    const progressEl = root.querySelector('.article-progress');
    const progressTrack = root.querySelector('.article-template__body') || content;

    if (showToc && headings.length && lists.length) {
      buildToc(headings, lists);
      if (desktopToc) desktopToc.hidden = false;
      if (mobileToc) mobileToc.hidden = false;

      const links = Array.from(root.querySelectorAll('.article-toc__link'));
      observeHeadings(headings, links);
      bindTocClicks(root, links);

      if (window.location.hash) {
        const hashId = window.location.hash.slice(1);
        if (headings.some((h) => h.id === hashId)) {
          setActiveLink(links, hashId);
        }
      } else if (headings[0]) {
        setActiveLink(links, headings[0].id);
      }
    } else if (showToc) {
      if (desktopToc) desktopToc.hidden = true;
      if (mobileToc) mobileToc.hidden = true;
    }

    if (showProgress && progressEl) {
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          updateProgress(progressEl, progressTrack);
          ticking = false;
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      updateProgress(progressEl, progressTrack);
    }
  }

  function init() {
    document.querySelectorAll('[data-article-enhancements]').forEach(initArticle);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
